// src/components/PTIM.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const EMPTY_PRODUCT = {
  productId: "",
  name: "",
  description: "",
  brand: "",
  type: "",
  category: "",
  source: "PTIM",
  status: "active",

  details: {
    capacity_litres: "",
    energy_efficiency_class: "",
    number_of_coils: 0,
    operating_pressure_bar: "",
    immersion_heater_rating_kw: "",
    warranty_years: "",
  },

  dimensions: {
    shell_height_mm: "",
    shell_diameter_mm: "",
    finished_height_mm: "",
    finished_diameter_mm: "",
    weight_empty_kg: "",
    weight_full_kg: "",
    insulation_thickness_mm: "",
  },

  assets: {
    sharepointFolderUrl: "",
    datasheetUrl: "",
    erpLabelUrl: "",
  },

  createdAt: null,
  updatedAt: null,
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function PTIM() {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | create | edit | copy

  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null); // { type, message }

  // ---- load products list ----
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProducts(true);
        const q = query(collection(db, "products"), orderBy("name"));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setProducts(list);
      } catch (err) {
        console.error(err);
        showToast("error", "Failed to load products.");
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const s = search.toLowerCase();
    return products.filter((p) => {
      return (
        (p.productId || p.id || "")
          .toString()
          .toLowerCase()
          .includes(s) ||
        (p.name || "").toLowerCase().includes(s) ||
        (p.brand || "").toLowerCase().includes(s)
      );
    });
  }, [products, search]);

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }

  function handleSelectProduct(id) {
    const found = products.find((p) => p.id === id);
    if (!found) return;

    setSelectedId(id);
    setMode("edit");

    // Normalise into our form shape
    setFormData({
      ...EMPTY_PRODUCT,
      ...found,
      productId: found.productId || found.id,
      details: {
        ...EMPTY_PRODUCT.details,
        ...(found.details || {}),
      },
      dimensions: {
        ...EMPTY_PRODUCT.dimensions,
        ...(found.dimensions || {}),
      },
      assets: {
        ...EMPTY_PRODUCT.assets,
        ...(found.assets || {}),
      },
    });
  }

  function handleNewProduct() {
    setSelectedId(null);
    setMode("create");
    setFormData({
      ...EMPTY_PRODUCT,
      productId: "",
    });
  }

  function handleCopyProduct() {
    if (!selectedId) {
      showToast("error", "Select a product to copy first.");
      return;
    }

    const base = products.find((p) => p.id === selectedId);
    if (!base) return;

    const copiedName = base.name ? `${base.name} (Copy)` : "";

    setMode("copy");
    setSelectedId(null);

    setFormData({
      ...EMPTY_PRODUCT,
      ...base,
      productId: "",
      name: copiedName,
      createdAt: null,
      updatedAt: null,
    });
  }

  function handleChange(field, value) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleNestedChange(path, value) {
    const parts = path.split(".");
    setFormData((prev) => {
      const clone = structuredClone(prev);
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (!cur[key]) cur[key] = {};
        cur = cur[key];
      }
      cur[parts[parts.length - 1]] = value;
      return clone;
    });
  }

  async function handleSave(e) {
    e?.preventDefault();

    if (!formData.productId || !formData.productId.trim()) {
      showToast("error", "Product ID is required.");
      return;
    }

    const targetId = formData.productId.trim();

    const payload = {
      ...formData,
      productId: targetId,
      updatedAt: serverTimestamp(),
    };

    if (!payload.createdAt) {
      payload.createdAt = serverTimestamp();
    }

    try {
      setSaving(true);
      const ref = doc(db, "products", targetId);

      // If we’re in create/copy mode, optionally check for collisions
      if (mode === "create" || mode === "copy") {
        const existing = await getDoc(ref);
        if (existing.exists()) {
          showToast(
            "error",
            `Product with ID "${targetId}" already exists. Choose a different ID.`
          );
          setSaving(false);
          return;
        }
      }

      await setDoc(ref, payload, { merge: true });

      // Update local list so it feels instant
      setProducts((prev) => {
        const idx = prev.findIndex((p) => p.id === targetId);
        const asPlain = { id: targetId, ...payload };
        if (idx === -1) return [...prev, asPlain].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...asPlain };
        return copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });

      setSelectedId(targetId);
      setMode("edit");
      showToast("success", "Product saved.");
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to save product.");
    } finally {
      setSaving(false);
    }
  }

  const modeLabel =
    mode === "create"
      ? "Creating new product"
      : mode === "copy"
      ? "Copying product"
      : mode === "edit"
      ? "Editing product"
      : "Select or create a product";

  const modeColor =
    mode === "create"
      ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/40"
      : mode === "copy"
      ? "bg-sky-500/10 text-sky-300 ring-sky-500/40"
      : mode === "edit"
      ? "bg-amber-500/10 text-amber-300 ring-amber-500/40"
      : "bg-slate-700/60 text-slate-300 ring-slate-500/30";

  return (
    <div className="h-[calc(100vh-4rem)] w-full bg-slate-950 text-slate-100 flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center shadow-md">
            <span className="text-xs font-semibold tracking-widest text-slate-200">
              PT
            </span>
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">
              PTIM – Product Information Manager
            </h1>
            <p className="text-xs text-slate-400">
              Create, clone and curate cylinders like a grown-up company.
            </p>
          </div>
        </div>
        <span
          className={classNames(
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ring-1",
            modeColor
          )}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current mr-2 animate-pulse" />
          {modeLabel}
        </span>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT: Product list / search */}
        <aside className="w-72 border-r border-slate-800 bg-slate-950/80 backdrop-blur-sm flex flex-col">
          <div className="p-3 border-b border-slate-800 space-y-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleNewProduct}
                className="flex-1 inline-flex items-center justify-center rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-semibold px-3 py-2 shadow-lg shadow-emerald-500/30 transition"
              >
                + New product
              </button>
              <button
                type="button"
                onClick={handleCopyProduct}
                className="inline-flex items-center justify-center rounded-lg border border-slate-700 text-xs font-medium px-3 py-2 hover:bg-slate-800/60 transition"
              >
                Copy
              </button>
            </div>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code, name, brand..."
                className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-1.5 text-xs placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">
                {filteredProducts.length}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-auto text-xs">
            {loadingProducts ? (
              <div className="p-4 text-slate-500 text-center text-xs">
                Loading products…
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-slate-500 text-center text-xs">
                No products found.
              </div>
            ) : (
              <ul className="divide-y divide-slate-800">
                {filteredProducts.map((p) => (
                  <li
                    key={p.id}
                    className={classNames(
                      "px-3 py-2 cursor-pointer group",
                      selectedId === p.id
                        ? "bg-slate-900/80"
                        : "hover:bg-slate-900/40"
                    )}
                    onClick={() => handleSelectProduct(p.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-[11px] truncate">
                          {p.name || "Untitled product"}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {(p.productId || p.id) +
                            (p.brand ? ` • ${p.brand}` : "")}
                        </p>
                      </div>
                      <span
                        className={classNames(
                          "text-[9px] px-1.5 py-0.5 rounded-full border",
                          p.status === "active"
                            ? "border-emerald-500/60 text-emerald-300/90 bg-emerald-500/5"
                            : "border-slate-600 text-slate-300 bg-slate-800/40"
                        )}
                      >
                        {p.status || "unknown"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* RIGHT: Editor */}
        <main className="flex-1 overflow-auto bg-slate-950/60">
          <form
            onSubmit={handleSave}
            className="max-w-5xl mx-auto px-6 py-5 space-y-6"
          >
            {/* BASIC INFO */}
            <section className="rounded-2xl border border-slate-800 bg-slate-950/80 shadow-xl shadow-black/40">
              <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold tracking-tight">
                    Core product info
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    Product identity & commercial metadata.
                  </p>
                </div>
                <div className="flex gap-2 text-[10px] text-slate-400">
                  <span>
                    ID:{" "}
                    <code className="text-slate-200">
                      {formData.productId || "—"}
                    </code>
                  </span>
                  <span className="hidden md:inline">
                    Mode: <span className="capitalize">{mode || "idle"}</span>
                  </span>
                </div>
              </div>

              <div className="px-5 py-4 grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Product ID / code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.productId}
                    onChange={(e) =>
                      handleChange("productId", e.target.value.toUpperCase())
                    }
                    placeholder="TSM-210-INDIRECT"
                  />
                  <p className="text-[10px] text-slate-500">
                    This becomes the Firestore document ID. Keep it stable and
                    unique.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Name
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Tempest 210L Indirect"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Brand
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.brand}
                    onChange={(e) => handleChange("brand", e.target.value)}
                    placeholder="Tempest"
                  />
                </div>

                <div className="space-y-2 grid grid-cols-3 gap-2 col-span-1 md:col-span-1">
                  <div className="space-y-1 col-span-1">
                    <label className="block text-[11px] font-medium text-slate-300">
                      Type
                    </label>
                    <select
                      className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      value={formData.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <option value="">—</option>
                      <option value="Indirect">Indirect</option>
                      <option value="Direct">Direct</option>
                      <option value="Twin Coil">Twin Coil</option>
                      <option value="Thermal Store">Thermal Store</option>
                      <option value="Buffer">Buffer</option>
                    </select>
                  </div>
                  <div className="space-y-1 col-span-1">
                    <label className="block text-[11px] font-medium text-slate-300">
                      Category
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      value={formData.category}
                      onChange={(e) =>
                        handleChange("category", e.target.value)
                      }
                      placeholder="Unvented cylinder"
                    />
                  </div>
                  <div className="space-y-1 col-span-1">
                    <label className="block text-[11px] font-medium text-slate-300">
                      Status
                    </label>
                    <select
                      className="w-full rounded-lg bg-slate-950 border border-slate-700 px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      value={formData.status}
                      onChange={(e) => handleChange("status", e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="obsolete">Obsolete</option>
                      <option value="draft">Draft</option>
                      <option value="hidden">Hidden</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    placeholder="Short technical/marketing description for internal tools."
                  />
                </div>
              </div>
            </section>

            {/* TECHNICAL DETAILS */}
            <section className="rounded-2xl border border-slate-800 bg-slate-950/80 shadow-xl shadow-black/40">
              <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold tracking-tight">
                    Technical details
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    Key technical metrics that drive selection & compliance.
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Capacity (L)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.capacity_litres}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.capacity_litres",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Energy efficiency class
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.energy_efficiency_class}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.energy_efficiency_class",
                        e.target.value.toUpperCase()
                      )
                    }
                    placeholder="B"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Number of coils
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={5}
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.number_of_coils}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.number_of_coils",
                        e.target.value ? Number(e.target.value) : 0
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Operating pressure (bar)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.operating_pressure_bar}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.operating_pressure_bar",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Immersion heater rating (kW)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.immersion_heater_rating_kw}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.immersion_heater_rating_kw",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Warranty (years)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.details.warranty_years}
                    onChange={(e) =>
                      handleNestedChange(
                        "details.warranty_years",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>
              </div>
            </section>

            {/* DIMENSIONS */}
            <section className="rounded-2xl border border-slate-800 bg-slate-950/80 shadow-xl shadow-black/40">
              <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold tracking-tight">
                    Dimensions & weights
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    Physical envelope, for drawings & site checks.
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Shell height (mm)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.dimensions.shell_height_mm}
                    onChange={(e) =>
                      handleNestedChange(
                        "dimensions.shell_height_mm",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Shell diameter (mm)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.dimensions.shell_diameter_mm}
                    onChange={(e) =>
                      handleNestedChange(
                        "dimensions.shell_diameter_mm",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Insulation thickness (mm)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.dimensions.insulation_thickness_mm}
                    onChange={(e) =>
                      handleNestedChange(
                        "dimensions.insulation_thickness_mm",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Weight empty (kg)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.dimensions.weight_empty_kg}
                    onChange={(e) =>
                      handleNestedChange(
                        "dimensions.weight_empty_kg",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Weight full (kg)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.dimensions.weight_full_kg}
                    onChange={(e) =>
                      handleNestedChange(
                        "dimensions.weight_full_kg",
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  />
                </div>
              </div>
            </section>

            {/* ASSETS */}
            <section className="rounded-2xl border border-slate-800 bg-slate-950/80 shadow-xl shadow-black/40 mb-20">
              <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold tracking-tight">
                    Datasheets & SharePoint
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    Link this product to its SharePoint folder and key PDFs.
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    SharePoint folder URL
                  </label>
                  <input
                    type="url"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.assets.sharepointFolderUrl}
                    onChange={(e) =>
                      handleNestedChange(
                        "assets.sharepointFolderUrl",
                        e.target.value
                      )
                    }
                    placeholder="https://sharepoint.company.com/sites/Products/Tempest/TSM-210-INDIRECT/"
                  />
                  <p className="text-[10px] text-slate-500">
                    Folder containing datasheet, ERP label, wiring diagrams,
                    etc.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    Datasheet URL (PDF)
                  </label>
                  <input
                    type="url"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.assets.datasheetUrl}
                    onChange={(e) =>
                      handleNestedChange("assets.datasheetUrl", e.target.value)
                    }
                    placeholder="https://sharepoint.../datasheet.pdf"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-slate-300">
                    ERP label URL (PDF)
                  </label>
                  <input
                    type="url"
                    className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={formData.assets.erpLabelUrl}
                    onChange={(e) =>
                      handleNestedChange("assets.erpLabelUrl", e.target.value)
                    }
                    placeholder="https://sharepoint.../erp-label.pdf"
                  />
                </div>
              </div>
            </section>

            {/* SAVE BAR */}
            <div className="fixed bottom-0 left-[18rem] right-0 z-20 bg-slate-950/95 border-t border-slate-800 backdrop-blur-sm">
              <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
                <div className="text-[11px] text-slate-400 truncate">
                  {mode === "edit" && selectedId ? (
                    <>
                      Editing{" "}
                      <span className="text-slate-200 font-medium">
                        {formData.name || formData.productId || selectedId}
                      </span>
                    </>
                  ) : mode === "create" ? (
                    <>Creating new product</>
                  ) : mode === "copy" ? (
                    <>Copying product into a new ID</>
                  ) : (
                    <>Select a product on the left or create a new one.</>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleNewProduct}
                    className="text-[11px] px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-900/80"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={classNames(
                      "inline-flex items-center justify-center text-[11px] font-semibold px-4 py-1.5 rounded-lg shadow-lg transition",
                      saving
                        ? "bg-emerald-600/70 text-emerald-50 cursor-wait"
                        : "bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-emerald-500/40"
                    )}
                  >
                    {saving ? "Saving…" : "Save product"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </main>
      </div>

      {/* TOAST */}
      {toast && (
        <div className="fixed bottom-4 left-4 z-30">
          <div
            className={classNames(
              "px-4 py-2 rounded-lg text-xs shadow-lg border",
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/60 text-emerald-100"
                : "bg-rose-500/10 border-rose-500/60 text-rose-100"
            )}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}
